import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'
import { AiOutlineInfoCircle } from 'react-icons/ai'

import { orders } from '../../api/services/orders'
import {
  OrderExtendedProfile,
  OrderWithPagination
} from '../../api/services/orders/types.ts'
import Alert from '../../components/common/Alert/Alert.tsx'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Card from '../../components/common/Card/Card.tsx'
import Loader from '../../components/common/Loader/Loader.tsx'
import { Pagination } from '../../components/common/Pagination'
import { usePaginatedQuery } from '../../components/common/Pagination/usePaginatedQuery.ts'
import Table from '../../components/common/Table/Table.tsx'
import FilterSection from '../../components/orders/FilterSection/FilterSection.tsx'
import { useAuth } from '../../context/auth'
import { checkRealizationDateWithCurrent } from '../../utils/dates.ts'
import {
  setOrdersTableActions,
  setOrdersTableColumns
} from '../../utils/ordersTableSettings.tsx'

const fetchOrders = async ({
  page,
  filters
}: {
  page: number
  filters?: string
}): Promise<OrderWithPagination | null> => {
  if (!filters) return null
  const filterParams = `?page=${page}${filters ? `${filters}` : ''}`
  return await orders.getOrders(filterParams)
}

export type ShownColumn<T> = {
  key: keyof T
  label: string
}

const WrapInCard = ({
  enabled,
  children
}: {
  enabled: boolean
  children: React.ReactNode
}) => {
  if (!enabled) return <>{children}</>
  return <Card className="mt-3">{children}</Card>
}

const Orders = ({
  patientProfileId,
  showColumns,
  breadcrumbs = true,
  wrapInCard = true
}: {
  patientProfileId?: number
  showColumns?: Array<ShownColumn<OrderExtendedProfile>>
  breadcrumbs?: boolean
  wrapInCard?: boolean
}) => {
  const { state } = useAuth()
  const [filterParams, setFilterParams] = useState<string | undefined>(
    undefined
  )

  useLayoutEffect(() => {
    if (patientProfileId)
      setFilterParams(`&patient_profile=${patientProfileId}`)
  }, [])

  const { data, isLoading, isError, currentPage, goToPage, totalPages } =
    usePaginatedQuery<OrderWithPagination>({
      queryKey: [
        'ordersWithProfiles',
        state.isAuthenticated,
        state.email,
        filterParams
      ],
      fetcher: ({ page, filterParams }) =>
        fetchOrders({ page, filters: filterParams }),
      pageSize: 10,
      filterParams
    })

  if (isError) {
    return <div>Wystąpił błąd podczas zapytania na serwer</div>
  }

  useEffect(() => {
    goToPage(1)
  }, [filterParams])

  const columns = useMemo(() => setOrdersTableColumns(showColumns), [])
  const actions = setOrdersTableActions()

  const getRowClassName = useCallback((row: OrderExtendedProfile): string => {
    const expiredStatus = checkRealizationDateWithCurrent(
      row.diet_start_date,
      row.status
    )
    if (expiredStatus === 'overdue')
      return 'bg-red-50 dark:bg-gray-900 dark:border-red-500 dark:border-2'
    if (expiredStatus === 'today')
      return 'dark:bg-gray-800 dark:border-yellow-500 dark:border-2'

    return ''
  }, [])

  return (
    <React.Fragment>
      {breadcrumbs && <Breadcrumbs pageName="Zamówienia" breadcrumbs={[]} />}

      {!patientProfileId && <FilterSection setFilterParams={setFilterParams} />}

      <WrapInCard enabled={wrapInCard}>
        <Card.Title>
          <div className="h-10 flex flex-row justify-between items-center">
            Lista zamówień
          </div>
        </Card.Title>
        <Card.Body>
          {isLoading && <Loader label={'Pobieranie danych...'} />}

          {data && !isLoading && data.results.length > 0 && (
            <>
              <Table
                columns={columns}
                data={data.results}
                actions={actions}
                rowClassName={getRowClassName}
              />
              <div className="mt-4 text-right">{`Liczba zamówień: ${data.count || 0}`}</div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={goToPage}
              />
            </>
          )}
          {!isLoading && data && data.results.length === 0 && (
            <div>
              <Alert
                type="info"
                message="Brak danych"
                icon={<AiOutlineInfoCircle className="text-lg" />}
              />
            </div>
          )}
        </Card.Body>
      </WrapInCard>
    </React.Fragment>
  )
}

export default Orders
